import { createContext } from 'preact';
import { useContext } from 'preact/hooks';

/**
 * The Hub's two languages (owner 2026-10-16: English stays the default, each person can pick Swedish in the account
 * menu). Every text lives in the dictionaries below; `sv` is typed against `en`, so a missing Swedish text fails the
 * typecheck instead of reaching staging. Pages move over one at a time.
 */
export type Lang = 'en' | 'sv';
export const LANG_KEY = 'kff.lang';

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

const en = {
  common: {
    loading: 'Loading…',
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving…',
    undo: 'Undo',
    undone: 'Undone',
    open: 'Open',
    today: 'Today',
    tomorrow: 'Tomorrow',
    moreOptions: 'More options',
    filled: (n: number) => `${n} filled in`,
    retry: 'Try again',
    errorGeneric: 'Something went wrong. Try again in a moment.',
    errorOffline: "Can't reach the Hub right now. Check the connection and try again.",
    actionFailed: "That didn't work",
    goBack: 'Back'
  },
  nav: {
    main: 'Main',
    groupProduction: 'Production',
    groupBusiness: 'Business',
    groupCompany: 'Company',
    myWork: 'My work',
    projects: 'Projects',
    clients: 'Clients',
    companies: 'Companies',
    contacts: 'Contacts',
    archive: 'Film archive',
    post: 'Post production',
    postGuide: 'Step by step',
    postTools: 'Tools and files',
    postAccounts: 'Accounts',
    postTips: 'Tips and lessons',
    quotes: 'Quotes',
    availability: 'Availability',
    capacity: 'Capacity',
    team: 'Team',
    people: 'People',
    peopleAccess: 'People & access',
    tickets: 'Tickets',
    settings: 'Settings',
    mySettings: 'My settings',
    whatsNew: "What's new",
    now: 'Now',
    news: 'News',
    myTime: 'My time',
    project: 'Project',
    videos: 'Videos',
    brief: 'Brief',
    shoots: 'Shoots',
    notes: 'Notes',
    files: 'Files',
    activity: 'Activity',
    details: 'Details',
    moreTabs: 'More',
    actors: 'Actors',
    library: 'Library',
    applications: 'Applications',
    priceList: 'Price list',
    deals: 'Deals',
    sales: 'Sales',
    contracts: 'Agreements',
    finance: 'Finance',
    financeOverview: 'Overview',
    toInvoice: 'To invoice',
    bokioCustomers: 'Customers in Bokio',
    agreementTerms: 'Agreement terms',
    costs: 'Costs',
    margins: 'Margin',
    agreementTemplates: 'Templates',
    modules: 'Modules',
    workloadSource: 'Team planning',
    connections: 'Connections',
    email: 'Sign-in and email',
    timeReporting: 'Time reporting',
    writeNews: 'Write news',
    invitePerson: 'Invite person'
  },
  cmd: {
    goTo: 'Go to',
    actions: 'Actions',
    writeNews: 'Write a news post',
    invitePerson: 'Invite person',
    startTimer: 'Start timer',
    logTime: 'Log time',
    logTimeHint: 'add time by hand',
    time: 'time',
    askKlara: 'Ask Klara',
    askKlaraHint: 'Help and tickets',
    theme: 'Theme',
    light: 'Light theme',
    dark: 'Dark theme',
    system: 'Follow system theme',
    account: 'Account',
    signOut: 'Sign out',
    language: 'Language',
    toSwedish: 'Svenska (Swedish)',
    toEnglish: 'English'
  },
  shell: {
    skip: 'Skip to content',
    more: 'More',
    searchLabel: 'Search and go to (Ctrl K)',
    searchBtn: 'Search or go to…',
    account: (name: string) => `Account: ${name}`,
    accountMenu: 'Account',
    theme: 'Theme',
    themes: { light: 'Light', dark: 'Dark', system: 'System' },
    language: 'Language',
    signOut: 'Sign out',
    viewAs: 'View as…',
    guide: 'Getting started',
    employee: 'Employee',
    freelancer: 'Freelancer',
    palette: 'Search and go to',
    palettePlaceholder: 'Search pages and actions…',
    results: 'Results',
    noMatch: (q: string) => `Nothing matches “${q}”.`,
    move: '↑↓ move',
    enter: '↵ open',
    esc: 'Esc close'
  },
  gate: {
    notYet: 'Not available yet',
    notForRole: 'Not for your role',
    goHome: 'Go to My work',
    forWho: (title: string, who: string) => `${title} is for ${who}. Ask an admin if you need it.`,
    blocked: (title: string) => `${title} is not switched on for your role in this early version of KFF Hub. It opens when that part of the Hub is ready – until then, keep using Monday for it.`,
    sellers: 'admins, producers, CD and sales',
    admins: 'admins',
    producersCd: 'admins, producers and CD',
    availabilityEmployees: "Availability is for freelancers. Employees' capacity comes from contracted hours minus absence in Personio.",
    notFound: 'Page not found',
    notFoundHead: "This page doesn't exist",
    notFoundBody: 'The link may be old or mistyped. Everything in the Hub is reachable from the menu.'
  },
  notify: {
    bell: (n: number) => (n ? `Notifications, ${n} unread` : 'Notifications'),
    title: 'Notifications',
    empty: 'Nothing new. Mentions, shoots and answers from clients show up here.',
    markAll: 'Mark all as read',
    failed: "Couldn't load notifications.",
    today: 'Today',
    yesterday: 'Yesterday',
    earlier: 'Earlier',
    close: 'Close',
    show: 'Show',
    openMeet: 'Open Meet',
    open: 'Open',
    snooze: 'In 5 min again',
    startsIn: (title: string, min: number) => (min <= 1 ? `${title} starts now` : `${title} starts in ${min} min`),
    shootStartsIn: (title: string, min: number) => (min <= 1 ? `Shoot ${title} starts now` : `Shoot ${title} starts in ${min} min`),
    held: (n: number) => (n === 1 ? 'While you were in a meeting: 1 notification' : `While you were in a meeting: ${n} notifications`),
    region: 'Notifications in the corner',
    topRegion: 'Meeting reminders',
    pingOn: 'Meetings ping in the browser 10 min before.',
    pingOff: 'Get a ping in the browser 10 min before meetings?',
    pingAllow: 'Turn on',
    pingAsk: 'Ping me in the browser',
    pingBlocked: 'The browser blocks notifications from the Hub. Allow them via the lock in the address bar.'
  },
  home: {
    dow: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    week: (n: number) => `week ${n}`,
    who: { Client: 'Client', CD: 'CD', Producer: 'Producer', Editor: 'Editing' } as Record<string, string>,
    showAll: (n: number) => `Show all ${n}`,
    showFewer: 'Show fewer',
    noDate: 'No date',
    daysLate: (n: number) => `${plural(n, 'day', 'days')} late`,
    days: (n: number) => plural(n, 'day', 'days'),
    nothingWaits: 'Nothing waits on you right now',
    needYou: (n: number) => `${plural(n, 'video needs', 'videos need')} you`,
    nLate: (n: number) => `${n} late`,
    nDueToday: (n: number) => `${n} due today`,
    earlyTitle: 'KFF Hub · early access',
    earlyBody: 'The Hub runs next to Monday for now. Projects, briefs and clients stay in Monday until those parts open here.',
    loadingVideos: 'Loading your videos',
    nothingForRole: 'Nothing here yet for your role.',
    side: 'Your week, absence and news',
    summary: 'Summary',
    yourTurn: 'Your turn',
    lateOldest: (n: number, oldest: string) => `${n} late · oldest ${oldest}`,
    nothingLate: 'nothing late',
    allClear: 'all clear',
    dueSoon: 'Due soon',
    inFiveDays: (next: string) => `in 5 working days · next ${next}`,
    nothingInFive: 'nothing in 5 working days',
    upNext: 'Up next',
    notDueYet: 'yours, not due yet',
    nothingQueued: 'nothing queued',
    waiting: 'Waiting on others',
    nothingOut: 'nothing out',
    allClearName: (name: string) => `All clear, ${name}.`,
    nothingWaitsDot: 'Nothing waits on you right now.',
    seeProjects: 'See all projects',
    upNextInfo: 'Yours once it is closer to the date',
    waitingOn: 'Waiting on',
    startTimerOn: (project: string) => `Start timer on ${project}`,
    startTimerTitle: 'Start timer on the project',
    openProject: 'Open project',
    openInZiflow: 'Open in Ziflow',
    openingZiflow: 'Opening Ziflow…',
    inZiflowTitle: 'This project is reviewed in Ziflow. Send the next version from the Hub and the project moves here.',
    myWeeks: 'My weeks',
    seeMyWeeks: 'See my weeks',
    openTeam: 'Open Team',
    thisWeek: (n: number) => `This week · W${n}`,
    weekShort: (n: number) => `W${n}`,
    noAvailability: (demand: string) => `${demand} · no availability`,
    ofCapacity: (pct: string, demand: string, cap: string) => `${pct} · ${demand} of ${cap}`,
    weeksSpan: (a: number, b: number) => `weeks ${a}–${b}`,
    nextTwoWeeks: 'the next two weeks',
    nobodyOver: (span: string) => `Nobody over capacity in ${span}`,
    nOver: (n: number, span: string) => `${n} over capacity in ${span}`
  },
  week: {
    dow: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    title: (n: number, span: string) => `Week ${n} · ${span}`,
    planned: 'Planned',
    plannedH: (h: string) => `Planned ${h}`,
    nToday: (n: number) => (n ? `${n} today` : 'nothing today'),
    allDay: 'All day',
    busyAllDay: 'Busy all day',
    busy: (from: string, to: string) => `Busy ${from}–${to}`,
    away: 'Away',
    morning: 'morning',
    afternoon: 'afternoon',
    requested: 'requested',
    due: 'Due',
    shoot: 'Shoot',
    loading: 'Loading your week',
    prev: 'Previous week',
    next: 'Next week',
    days: 'Days',
    todayMark: ' (today)',
    holiday: 'Public holiday',
    nothing: 'Nothing planned',
    more: (n: number) => `+${n} more`,
    less: 'Show less',
    calError: 'Calendar not available right now · busy time from Google is missing',
    calOff: 'Google Calendar is not connected · busy time shows here once it is'
  },
  newsNow: {
    title: 'News',
    all: 'All news',
    newOne: (title: string) => `New post: ${title}`,
    newMany: (n: number, title: string) => `${n} new posts · ${title}`,
    read: 'Read',
    unread: 'Unread: ',
    none: 'No news yet.'
  }
};

export type Dict = typeof en;

const sv: Dict = {
  common: {
    loading: 'Laddar…',
    cancel: 'Avbryt',
    save: 'Spara',
    saving: 'Sparar…',
    undo: 'Ångra',
    undone: 'Ångrat',
    open: 'Öppna',
    today: 'I dag',
    tomorrow: 'I morgon',
    moreOptions: 'Fler val',
    filled: (n: number) => `${n} ifyllda`,
    retry: 'Försök igen',
    errorGeneric: 'Något gick fel. Försök igen om en stund.',
    errorOffline: 'Hubben går inte att nå just nu. Kontrollera uppkopplingen och försök igen.',
    actionFailed: 'Det gick inte',
    goBack: 'Tillbaka'
  },
  nav: {
    main: 'Huvudmeny',
    groupProduction: 'Produktion',
    groupBusiness: 'Affär',
    groupCompany: 'Bolaget',
    myWork: 'Mitt arbete',
    projects: 'Projekt',
    clients: 'Kunder',
    companies: 'Företag',
    contacts: 'Kontakter',
    archive: 'Filmarkiv',
    post: 'Postproduktion',
    postGuide: 'Steg för steg',
    postTools: 'Verktyg och filer',
    postAccounts: 'Konton',
    postTips: 'Tips och lärdomar',
    quotes: 'Offerter',
    availability: 'Tillgänglighet',
    capacity: 'Kapacitet',
    team: 'Team',
    people: 'Personer',
    peopleAccess: 'Personer och behörighet',
    tickets: 'Ärenden',
    settings: 'Inställningar',
    mySettings: 'Mina inställningar',
    whatsNew: 'Vad är nytt',
    now: 'Nu',
    news: 'Nyheter',
    myTime: 'Min tid',
    project: 'Projekt',
    videos: 'Videor',
    brief: 'Brief',
    shoots: 'Inspelningar',
    notes: 'Anteckningar',
    files: 'Filer',
    activity: 'Aktivitet',
    details: 'Detaljer',
    moreTabs: 'Mer',
    actors: 'Skådespelare',
    library: 'Bibliotek',
    applications: 'Ansökningar',
    priceList: 'Prislista',
    deals: 'Affärer',
    sales: 'Sälj',
    contracts: 'Avtal',
    finance: 'Ekonomi',
    financeOverview: 'Översikt',
    toInvoice: 'Att fakturera',
    bokioCustomers: 'Kunder i Bokio',
    agreementTerms: 'Avtalsvillkor',
    costs: 'Kostnader',
    margins: 'Marginal',
    agreementTemplates: 'Mallar',
    modules: 'Moduler',
    workloadSource: 'Planering i Team',
    connections: 'Kopplingar',
    email: 'Inloggning och e-post',
    timeReporting: 'Tidrapportering',
    writeNews: 'Skriv nyheter',
    invitePerson: 'Bjud in person'
  },
  cmd: {
    goTo: 'Gå till',
    actions: 'Åtgärder',
    writeNews: 'Skriv en nyhet',
    invitePerson: 'Bjud in person',
    startTimer: 'Starta timer',
    logTime: 'Logga tid',
    logTimeHint: 'lägg till tid för hand',
    time: 'tid',
    askKlara: 'Fråga Klara',
    askKlaraHint: 'Hjälp och ärenden',
    theme: 'Tema',
    light: 'Ljust tema',
    dark: 'Mörkt tema',
    system: 'Följ systemets tema',
    account: 'Konto',
    signOut: 'Logga ut',
    language: 'Språk',
    toSwedish: 'Svenska',
    toEnglish: 'English (engelska)'
  },
  shell: {
    skip: 'Hoppa till innehållet',
    more: 'Mer',
    searchLabel: 'Sök och gå till (Ctrl K)',
    searchBtn: 'Sök eller gå till…',
    account: (name: string) => `Konto: ${name}`,
    accountMenu: 'Konto',
    theme: 'Tema',
    themes: { light: 'Ljust', dark: 'Mörkt', system: 'System' },
    language: 'Språk',
    signOut: 'Logga ut',
    viewAs: 'Visa som…',
    guide: 'Kom igång',
    employee: 'Anställd',
    freelancer: 'Frilansare',
    palette: 'Sök och gå till',
    palettePlaceholder: 'Sök sidor och åtgärder…',
    results: 'Resultat',
    noMatch: (q: string) => `Inget matchar ”${q}”.`,
    move: '↑↓ flytta',
    enter: '↵ öppna',
    esc: 'Esc stäng'
  },
  gate: {
    notYet: 'Inte tillgängligt än',
    notForRole: 'Inte för din roll',
    goHome: 'Gå till Mitt arbete',
    forWho: (title: string, who: string) => `${title} är för ${who}. Fråga en admin om du behöver det.`,
    blocked: (title: string) => `${title} är inte påslaget för din roll i den här tidiga versionen av KFF Hub. Det öppnar när den delen av Hubben är klar – fram till dess använder du Monday för det.`,
    sellers: 'admin, producenter, CD och sälj',
    admins: 'admin',
    producersCd: 'admin, producenter och CD',
    availabilityEmployees: 'Tillgänglighet är för frilansare. Anställdas kapacitet kommer från avtalade timmar minus frånvaro i Personio.',
    notFound: 'Sidan hittades inte',
    notFoundHead: 'Sidan finns inte',
    notFoundBody: 'Länken kan vara gammal eller felskriven. Allt i Hubben nås från menyn.'
  },
  notify: {
    bell: (n: number) => (n ? `Notiser, ${n} olästa` : 'Notiser'),
    title: 'Notiser',
    empty: 'Inget nytt. Omnämnanden, inspelningar och svar från kunder hamnar här.',
    markAll: 'Markera alla som lästa',
    failed: 'Notiserna kunde inte hämtas.',
    today: 'I dag',
    yesterday: 'I går',
    earlier: 'Tidigare',
    close: 'Stäng',
    show: 'Visa',
    openMeet: 'Öppna Meet',
    open: 'Öppna',
    snooze: 'Om 5 min igen',
    startsIn: (title: string, min: number) => (min <= 1 ? `${title} börjar nu` : `${title} börjar om ${min} min`),
    shootStartsIn: (title: string, min: number) => (min <= 1 ? `Inspelningen ${title} börjar nu` : `Inspelningen ${title} börjar om ${min} min`),
    held: (n: number) => (n === 1 ? 'Medan du satt i möte: 1 notis' : `Medan du satt i möte: ${n} notiser`),
    region: 'Notiser i hörnet',
    topRegion: 'Mötespåminnelser',
    pingOn: 'Möten pingar i webbläsaren 10 min före.',
    pingOff: 'Vill du få en ping i webbläsaren 10 min före möten?',
    pingAllow: 'Slå på',
    pingAsk: 'Pinga mig i webbläsaren',
    pingBlocked: 'Webbläsaren blockerar notiser från Hubben. Tillåt dem via låset i adressfältet.'
  },
  home: {
    dow: ['söndag', 'måndag', 'tisdag', 'onsdag', 'torsdag', 'fredag', 'lördag'],
    months: ['januari', 'februari', 'mars', 'april', 'maj', 'juni', 'juli', 'augusti', 'september', 'oktober', 'november', 'december'],
    week: (n: number) => `vecka ${n}`,
    who: { Client: 'Kund', CD: 'CD', Producer: 'Producent', Editor: 'Redigering' } as Record<string, string>,
    showAll: (n: number) => `Visa alla ${n}`,
    showFewer: 'Visa färre',
    noDate: 'Inget datum',
    daysLate: (n: number) => `Försenad ${plural(n, 'dag', 'dagar')}`,
    days: (n: number) => plural(n, 'dag', 'dagar'),
    nothingWaits: 'Inget väntar på dig just nu',
    needYou: (n: number) => `${plural(n, 'video', 'videor')} väntar på dig`,
    nLate: (n: number) => `${n} försenade`,
    nDueToday: (n: number) => `${n} med deadline i dag`,
    earlyTitle: 'KFF Hub · tidig version',
    earlyBody: 'Hubben körs bredvid Monday än så länge. Projekt, briefer och kunder ligger kvar i Monday tills de delarna öppnar här.',
    loadingVideos: 'Laddar dina videor',
    nothingForRole: 'Inget här än för din roll.',
    side: 'Din vecka, frånvaro och nyheter',
    summary: 'Sammanfattning',
    yourTurn: 'Din tur',
    lateOldest: (n: number, oldest: string) => `${n} försenade · äldsta ${oldest}`,
    nothingLate: 'inget försenat',
    allClear: 'allt klart',
    dueSoon: 'Snart deadline',
    inFiveDays: (next: string) => `inom 5 arbetsdagar · nästa ${next}`,
    nothingInFive: 'inget inom 5 arbetsdagar',
    upNext: 'Härnäst',
    notDueYet: 'dina, inte aktuella än',
    nothingQueued: 'inget i kö',
    waiting: 'Väntar på andra',
    nothingOut: 'inget ute',
    allClearName: (name: string) => `Allt klart, ${name}.`,
    nothingWaitsDot: 'Inget väntar på dig just nu.',
    seeProjects: 'Se alla projekt',
    upNextInfo: 'Blir din tur närmare datumet',
    waitingOn: 'Väntar på',
    startTimerOn: (project: string) => `Starta timer på ${project}`,
    startTimerTitle: 'Starta timer på projektet',
    openProject: 'Öppna projekt',
    openInZiflow: 'Öppna i Ziflow',
    openingZiflow: 'Öppnar Ziflow…',
    inZiflowTitle: 'Projektet granskas i Ziflow. Skicka nästa version från Hubben så flyttas projektet hit.',
    myWeeks: 'Mina veckor',
    seeMyWeeks: 'Se mina veckor',
    openTeam: 'Öppna Team',
    thisWeek: (n: number) => `Den här veckan · v. ${n}`,
    weekShort: (n: number) => `v. ${n}`,
    noAvailability: (demand: string) => `${demand} · ingen tillgänglighet`,
    ofCapacity: (pct: string, demand: string, cap: string) => `${pct} · ${demand} av ${cap}`,
    weeksSpan: (a: number, b: number) => `vecka ${a}–${b}`,
    nextTwoWeeks: 'de närmaste två veckorna',
    nobodyOver: (span: string) => `Ingen över kapacitet ${span}`,
    nOver: (n: number, span: string) => `${n} över kapacitet ${span}`
  },
  week: {
    dow: ['sön', 'mån', 'tis', 'ons', 'tors', 'fre', 'lör'],
    months: ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'],
    title: (n: number, span: string) => `Vecka ${n} · ${span}`,
    planned: 'Planerat',
    plannedH: (h: string) => `Planerat ${h}`,
    nToday: (n: number) => (n ? `${n} i dag` : 'inget i dag'),
    allDay: 'Heldag',
    busyAllDay: 'Upptagen hela dagen',
    busy: (from: string, to: string) => `Upptagen ${from}–${to}`,
    away: 'Ledig',
    morning: 'förmiddag',
    afternoon: 'eftermiddag',
    requested: 'begärd',
    due: 'Deadline',
    shoot: 'Inspelning',
    loading: 'Laddar din vecka',
    prev: 'Föregående vecka',
    next: 'Nästa vecka',
    days: 'Dagar',
    todayMark: ' (i dag)',
    holiday: 'Helgdag',
    nothing: 'Inget planerat',
    more: (n: number) => `+${n} till`,
    less: 'Visa mindre',
    calError: 'Kalendern går inte att nå just nu · upptagen tid från Google saknas',
    calOff: 'Google Kalender är inte ansluten · upptagen tid visas här när den är det'
  },
  newsNow: {
    title: 'Nyheter',
    all: 'Alla nyheter',
    newOne: (title: string) => `Ny nyhet: ${title}`,
    newMany: (n: number, title: string) => `${n} nya nyheter · ${title}`,
    read: 'Läs',
    unread: 'Oläst: ',
    none: 'Inga nyheter än.'
  }
};

export const DICT: Record<Lang, Dict> = { en, sv };

/** A page's own texts, kept next to the page: Swedish is typed against English, like the shared dictionary above. */
export const texts = <T>(en: T, sv: NoInfer<T>): Record<Lang, T> => ({ en, sv });

/** The signed-in person's language; English outside a provider (tests, the sign-in page). */
export const LangContext = createContext<Lang>('en');
export const useLang = (): Lang => useContext(LangContext);
export const useT = (): Dict => DICT[useContext(LangContext)];

/**
 * The language remembered on this device, so the first paint is right before /api/me answers; with nothing remembered
 * (the sign-in page on a new device) the browser's own language decides.
 */
export function storedLang(): Lang {
  try {
    const l = localStorage.getItem(LANG_KEY);
    if (l === 'sv' || l === 'en') return l;
  } catch {
    /* private mode */
  }
  return browserLang();
}

/** Swedish when the browser prefers Swedish over English, else English. */
export function browserLang(): Lang {
  if (typeof navigator === 'undefined') return 'en';
  const prefs = navigator.languages?.length ? navigator.languages : [navigator.language];
  const first = prefs.find((x) => /^(sv|en)\b/i.test(x ?? ''));
  return first && /^sv/i.test(first) ? 'sv' : 'en';
}
export function rememberLang(l: Lang): void {
  try {
    localStorage.setItem(LANG_KEY, l);
  } catch {
    /* private mode – the choice is still saved on the person */
  }
  if (typeof document !== 'undefined') document.documentElement.lang = l;
}
